import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { UserDisplay } from "@components/common/user-display";
import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { DeleteIcon, Dialog, MenuItem, Text, useToast } from "@flat-ui";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { removeMembersFromTeam } from "@store/teams/teams-slice-thunk";
import { formatUserRoleType } from "@utils/data-display";
import { TableType, TeamMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
/**
 * Menu item that opens a confirmation dialog to remove a single member from a
 * team. Used in the member profile "Teams" tab, where each row is a team the
 * member belongs to.
 */
export function RemoveMemberFromTeamMenuButton({
  companyId,
  team,
  member,
  onMenuClose
}) {
  const dispatch = useAppDispatch();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    isRemovingTeamMember
  } = useAppSelector(fetchingTeamsFlagsSelector);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  function closeDialog() {
    setIsDialogOpen(false);
    onMenuClose();
  }
  async function confirmRemove() {
    trackEvent({
      name: TeamMemberEvents.removeMemberFromTeam,
      props: {
        dataType: TableType.teams
      }
    });
    try {
      await dispatch(removeMembersFromTeam({
        companyId,
        teamId: team.id,
        memberIds: [member.identity]
      })).unwrap();
      openToast({
        title: "Member removed from team",
        message: <RemoveMemberSuccessDescription member={member} subjectType="team" entityName={team.name} />
      });
    } catch {
      // Errors are handled by the slice
    } finally {
      closeDialog();
    }
  }

  // A team assigns a workspace role only when it is not the default "MEMBER".
  const hasWorkspaceRole = team.role !== "MEMBER";
  return <>
      <MenuItem label="Remove from team" Icon={DeleteIcon} onClick={() => setIsDialogOpen(true)} />

      <Dialog title="Remove from team" confirmText="Remove" open={isDialogOpen} onConfirm={confirmRemove} onCancel={closeDialog} showSpinner={isRemovingTeamMember}>
        <Text variant="bodyM" sx={{
        marginBottom: SPACE_ELEMENTS_OF_MODAL
      }}>
          The member will lose access to all projects that this team is a part
          of
          {hasWorkspaceRole && <>
              {" and will lose the "}
              <strong>{formatUserRoleType(team.role)}</strong>
              {" workspace role assigned through this team"}
            </>}
          .
        </Text>

        <UserDisplay member={member} />
      </Dialog>
    </>;
}